import React, { FC, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import { I18n } from '@lingui/core';
import { Link, navigate } from 'gatsby';
import { useLingui } from '@lingui/react';
import Form from '@/components/Common/Form';
import { TextField } from '@/components/Common/Field';
import { useFormState } from '@/hooks/useFormState';
import Button, { ButtonVariant } from '@/components/Common/Button';
import { commonRules } from '@/utils/validation';
import { useAuthentication } from '@/context/Authentication';
import * as user from '@/requests/user';
import routes from '@/routes';
import useApi from '@/hooks/useApi';
import PasswordField from '@/components/Common/Field/PasswordField';
import { ResultStatus } from '@/types/api';
import Notice, { NoticeType } from '@/components/Common/Notice';
import { User, UserType } from '@/types/index';
import { PropsWithClassName } from '@/types/common';
import { isWorkplaceUser } from '@/utils/user';
import * as styles from './LoginForm.module.scss';

type Props = PropsWithClassName<{
  onSuccess?: (user: User) => void;
  isSendingWishlist?: boolean;
  redirectAfterRegister?: string;
}>;

enum ErrorMessage {
  IncorrectCredentials = 'incorrectCredentials',
  General = 'general',
}

const errorMessages = (i18n: I18n) => ({
  [ErrorMessage.IncorrectCredentials]: i18n._(
    t`Wrong email or password, please try again. First time? Check your email to set a password.`
  ),
  [ErrorMessage.General]: i18n._(
    t`There was an error while trying to log in. Please try again later.`
  ),
});

const LoginForm: FC<Props> = ({
  className,
  redirectAfterRegister,
  onSuccess,
  isSendingWishlist,
}) => {
  const { i18n } = useLingui();
  const [errors, setErrors] = useState('');
  const { isLoading, errors: apiErrors, request } = useApi(user.authenticate);
  const { authenticate, setRedirectAfterSignInPath } = useAuthentication();
  const {
    connect,
    values,
    hasErrors: hasFormErrors,
  } = useFormState(
    {
      email: '',
      password: '',
    },
    {
      onChange: () => setErrors(''),
    }
  );

  const messages = errorMessages(i18n);

  const handleSubmit = async () => {
    try {
      const result = await request(values);
      if (
        result?.status === ResultStatus.Success &&
        result?.data &&
        isWorkplaceUser(result.data.user)
      ) {
        authenticate(result.data.user, result.data.accessToken, result.data.refreshToken);
        onSuccess?.(result.data.user);
      } else {
        setErrors(messages[ErrorMessage.IncorrectCredentials]);
      }
    } catch (e) {
      setErrors(messages[ErrorMessage.General]);
      console.log(e);
    }
  };

  const handleRegisterClick = () => {
    if (redirectAfterRegister) {
      setRedirectAfterSignInPath(redirectAfterRegister);
    }
    navigate(
      isSendingWishlist
        ? routes.register({ allow: UserType.Single, wishlist: true })
        : routes.register()
    );
  };

  return (
    <div className={className}>
      <Form>
        {errors || apiErrors?.length ? (
          <Notice type={NoticeType.Error}>
            {errors ?? messages[ErrorMessage.General]}
          </Notice>
        ) : null}

        <TextField
          className={styles.field}
          {...connect('email')}
          label={i18n._(t`Email`)}
          validate={[commonRules.email]}
        />
        <PasswordField
          className={styles.field}
          {...connect('password')}
          validate={[commonRules.required]}
          label={i18n._(t`Password`)}
          inputProps={{
            type: 'password',
          }}
        />
        <Link className={styles.forgottenPassword} to={routes.forgotPassword()}>
          <Trans>Forgotten password?</Trans>
        </Link>
        <Button
          onClick={handleSubmit}
          className={styles.button}
          type="submit"
          variant={ButtonVariant.Primary}
          disabled={isLoading || hasFormErrors}
          isLoading={isLoading}
        >
          <Trans>Log in</Trans>
        </Button>
      </Form>
      <div className={styles.createAccount}>
        <h3>
          <Trans>New to Kettal?</Trans>
        </h3>
        <p>
          <Trans>
            With an account, you can save products to make your list for projects,
            download materials, etc...
          </Trans>
        </p>
        <Button
          className={styles.button}
          onClick={handleRegisterClick}
          variant={ButtonVariant.Ghost}
        >
          <Trans>Create account</Trans>
        </Button>
      </div>
    </div>
  );
};

export default LoginForm;
